import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Backdrop, CircularProgress, Box, Typography } from '@mui/material';
import { tokens } from '../../theme/tokens';

interface LoaderContextType {
  isLoading: boolean;
  message: string;
  setLoading: (loading: boolean, message?: string) => void;
}

const LoaderContext = createContext<LoaderContextType>({
  isLoading: false,
  message: '',
  setLoading: () => {},
});

/**
 * Custom hook to access the global loader
 */
export const useLoader = () => {
  const context = useContext(LoaderContext);
  if (!context) {
    throw new Error('useLoader must be used within a LoaderProvider');
  }
  return context;
};

interface LoaderProviderProps {
  children: ReactNode;
}

/**
 * LoaderProvider Component
 * Provides global loading state management
 */
export const LoaderProvider: React.FC<LoaderProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const setLoading = (loading: boolean, msg: string = '') => {
    setIsLoading(loading);
    setMessage(msg);
  };

  return (
    <LoaderContext.Provider value={{ isLoading, message, setLoading }}>
      {children}
    </LoaderContext.Provider>
  );
};

/**
 * GlobalLoader Component
 * Displays a full-screen loading indicator
 */
export const GlobalLoader: React.FC = () => {
  const { isLoading, message } = useLoader();

  return (
    <Backdrop
      open={isLoading}
      role="status"
      aria-live="polite"
      aria-label={message || 'Loading, please wait'}
      sx={{
        zIndex: tokens.zIndex.modal + 1,
        bgcolor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.spacing.lg,
        }}
      >
        <CircularProgress
          size={60}
          thickness={4}
          aria-hidden="true"
          sx={{
            color: 'white',
          }}
        />
        {message && (
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: tokens.typography.fontWeight.medium,
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </Backdrop>
  );
};

export default GlobalLoader;
